import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  ArrowPathIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { tasksAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

interface RecurringTaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  recurringTask: any | null;
  onEdit: () => void;
  onDelete: () => void;
  currentUser: any;
}

const RecurringTaskDetailsModal: React.FC<RecurringTaskDetailsModalProps> = ({
  isOpen,
  onClose,
  recurringTask,
  onEdit,
  onDelete,
  currentUser,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'feedbacks' | 'finish-details'>('details');
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [finishDetails, setFinishDetails] = useState<any[]>([]);
  const [newFeedback, setNewFeedback] = useState('');
  const [newFinishDetail, setNewFinishDetail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (recurringTask && isOpen) {
      loadFeedbacksAndFinishDetails();
    }
  }, [recurringTask, isOpen]);

  const loadFeedbacksAndFinishDetails = async () => {
    if (!recurringTask) return;

    try {
      const [feedbacksData, finishDetailsData] = await Promise.all([
        tasksAPI.getRecurringTaskFeedbacks(recurringTask.id),
        tasksAPI.getRecurringTaskFinishDetails(recurringTask.id),
      ]);

      setFeedbacks(feedbacksData);
      setFinishDetails(finishDetailsData);
    } catch (error) {
      console.error('Error loading feedbacks and finish details:', error);
      toast.error('Failed to load feedbacks and finish details');
    }
  };

  const handleAddFeedback = async () => {
    if (!newFeedback.trim() || !recurringTask || !currentUser) return;

    try {
      setIsLoading(true);
      await tasksAPI.addRecurringTaskFeedback(recurringTask.id, newFeedback, currentUser.id);
      setNewFeedback('');
      toast.success('Feedback added successfully');
      loadFeedbacksAndFinishDetails();
    } catch (error) {
      console.error('Error adding feedback:', error);
      toast.error('Failed to add feedback');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFinishDetail = async () => {
    if (!newFinishDetail.trim() || !recurringTask || !currentUser) return;

    try {
      setIsLoading(true);
      await tasksAPI.addRecurringTaskFinishDetail(recurringTask.id, newFinishDetail, currentUser.id);
      setNewFinishDetail('');
      toast.success('Finish detail added successfully');
      loadFeedbacksAndFinishDetails();
    } catch (error) {
      console.error('Error adding finish detail:', error);
      toast.error('Failed to add finish detail');
    } finally {
      setIsLoading(false);
    }
  };

  const formatRecurrencePattern = (task: any) => {
    if (!task) return '';
    
    let pattern = `Every ${task.recurrenceValue} ${task.recurrenceType.toLowerCase()}`;
    
    if (task.recurrenceType === 'WEEKLY' && task.daysOfWeek) {
      const days = JSON.parse(task.daysOfWeek);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const selectedDays = days.map((d: number) => dayNames[d]).join(', ');
      pattern += ` on ${selectedDays}`;
    }
    
    if (task.recurrenceType === 'MONTHLY' && task.dayOfMonth) {
      pattern += ` on day ${task.dayOfMonth}`;
    }
    
    return pattern;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'NORMAL': return 'bg-blue-100 text-blue-800';
      case 'LOW': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return 'bg-gray-100 text-gray-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'REVIEW': return 'bg-yellow-100 text-yellow-800';
      case 'DONE': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen || !recurringTask) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ArrowPathIcon className="w-6 h-6 text-blue-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{recurringTask.title}</h2>
              <p className="text-sm text-gray-500">{formatRecurrencePattern(recurringTask)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
              title="Edit Recurring Task"
            >
              <PencilIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete Recurring Task"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'details', name: 'Details', icon: ArrowPathIcon },
              { id: 'feedbacks', name: 'Feedbacks', icon: ChatBubbleLeftRightIcon },
              { id: 'finish-details', name: 'Finish Details', icon: CheckCircleIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Status and Priority */}
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(recurringTask.status)}`}>
                  {recurringTask.status.replace('_', ' ')}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getPriorityColor(recurringTask.priority)}`}>
                  {recurringTask.priority}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                  recurringTask.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {recurringTask.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Description */}
              {recurringTask.description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <p className="text-gray-900">{recurringTask.description}</p>
                </div>
              )}

              {/* Recurrence Details */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Recurrence Pattern</h3>
                <p className="text-gray-900">{formatRecurrencePattern(recurringTask)}</p>
                {recurringTask.endDate && (
                  <p className="text-sm text-gray-600 mt-1">
                    Ends on {new Date(recurringTask.endDate).toLocaleDateString()}
                  </p>
                )}
                {recurringTask.maxOccurrences && (
                  <p className="text-sm text-gray-600 mt-1">
                    Maximum {recurringTask.maxOccurrences} occurrences
                  </p>
                )}
              </div>

              {/* Assignment Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Team */}
                {recurringTask.team && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Team</h3>
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: recurringTask.team.color }}
                      />
                      <span className="text-gray-900">{recurringTask.team.name}</span>
                    </div>
                  </div>
                )}

                {/* Project */}
                {recurringTask.project && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Project</h3>
                    <span className="text-gray-900">{recurringTask.project.name}</span>
                  </div>
                )}
              </div>

              {/* Assignees */}
              {recurringTask.assignees && recurringTask.assignees.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Assignees</h3>
                  <div className="flex flex-wrap gap-2">
                    {recurringTask.assignees.map((assignment: any) => (
                      <div
                        key={assignment.id}
                        className="flex items-center space-x-2 bg-gray-100 rounded-full px-3 py-1"
                      >
                        <UserIcon className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">
                          {assignment.user.firstName} {assignment.user.lastName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {recurringTask.estimatedHours && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Estimated Hours</h3>
                    <div className="flex items-center space-x-1">
                      <ClockIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-900">{recurringTask.estimatedHours}h</span>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Created</h3>
                  <div className="flex items-center space-x-1">
                    <CalendarIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-900">
                      {new Date(recurringTask.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Creator</h3>
                  <div className="flex items-center space-x-1">
                    <UserIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-900">
                      {recurringTask.creator.firstName} {recurringTask.creator.lastName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'feedbacks' && (
            <div className="space-y-6">
              {/* Add Feedback */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Add Feedback</h3>
                <div className="space-y-3">
                  <textarea
                    value={newFeedback}
                    onChange={(e) => setNewFeedback(e.target.value)}
                    placeholder="Share your feedback about this recurring task..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddFeedback}
                    disabled={!newFeedback.trim() || isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? 'Adding...' : 'Add Feedback'}
                  </button>
                </div>
              </div>

              {/* Feedbacks List */}
              <div className="space-y-4">
                {feedbacks.length === 0 ? (
                  <div className="text-center py-8">
                    <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Feedbacks Yet</h3>
                    <p className="text-gray-600">Be the first to share feedback about this recurring task.</p>
                  </div>
                ) : (
                  feedbacks.map((feedback) => (
                    <div key={feedback.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="w-8 h-8 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {feedback.author.firstName} {feedback.author.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(feedback.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {currentUser && feedback.author.id === currentUser.id && (
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this feedback?')) {
                                try {
                                  await tasksAPI.deleteRecurringTaskFeedback(feedback.id, currentUser.id);
                                  toast.success('Feedback deleted successfully');
                                  loadFeedbacksAndFinishDetails();
                                } catch (error) {
                                  console.error('Error deleting feedback:', error);
                                  toast.error('Failed to delete feedback');
                                }
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete Feedback"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className="mt-3 text-gray-700">{feedback.content}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'finish-details' && (
            <div className="space-y-6">
              {/* Add Finish Detail */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Add Finish Detail</h3>
                <div className="space-y-3">
                  <textarea
                    value={newFinishDetail}
                    onChange={(e) => setNewFinishDetail(e.target.value)}
                    placeholder="Add details about task completion..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddFinishDetail}
                    disabled={!newFinishDetail.trim() || isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? 'Adding...' : 'Add Finish Detail'}
                  </button>
                </div>
              </div>

              {/* Finish Details List */}
              <div className="space-y-4">
                {finishDetails.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Finish Details Yet</h3>
                    <p className="text-gray-600">Add details about task completion when instances are finished.</p>
                  </div>
                ) : (
                  finishDetails.map((detail) => (
                    <div key={detail.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="w-8 h-8 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {detail.author.firstName} {detail.author.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(detail.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {currentUser && detail.author.id === currentUser.id && (
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this finish detail?')) {
                                try {
                                  await tasksAPI.deleteRecurringTaskFinishDetail(detail.id, currentUser.id);
                                  toast.success('Finish detail deleted successfully');
                                  loadFeedbacksAndFinishDetails();
                                } catch (error) {
                                  console.error('Error deleting finish detail:', error);
                                  toast.error('Failed to delete finish detail');
                                }
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete Finish Detail"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className="mt-3 text-gray-700">{detail.content}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default RecurringTaskDetailsModal;
